import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GameInfoIcon } from '@swf/casino';
import useTouchSupported from '@swf/core/hooks/useTouchSupported';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { PlayType } from '@swf/core/types/CasinoGame';

import styles from './CasinoPlaySelector.scss';
import FavoriteGameIcon from './FavoriteGameIcon/FavoriteGameIcon';
import type { Props as PlayButtonPros } from './PlayButton/PlayButton';
import PlayButton from './PlayButton/PlayButton';
import RoundPlayButton from './RoundPlayButton/RoundPlayButton';

export interface Props {
  className?: string;
  count?: number;
  favouriteIcon?: JSX.Element;
  infoIconClass?: string;
  game?: CasinoGame;
  hideFeatureButtons?: boolean;
  hover?: boolean;
  index?: number;
  multiplayer?: boolean;
  onClick(playType?: PlayType): void;
  size?: PlayButtonPros['size'];
}

const CasinoPlaySelector: React.FC<Props> = ({
  className,
  infoIconClass,
  hover,
  game,
  multiplayer,
  favouriteIcon,
  index,
  count,
  hideFeatureButtons,
  ...rest
}) => {
  const { casino } = useDispatch<Dispatch>();

  const favoritesEnabled = useFeature(FeatureFlag.CasinoFavorites);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const isTouchSupported = useTouchSupported();
  const { widgetId } = useWidget();
  const { gametype = 0, variant = 0, isFavorite = false, name } = game || {};

  const onFavoriteClick = useCallback(() => {
    if (gametype && variant)
      casino.toggleFavoriteGame({
        gameTypeId: gametype,
        gameVariantId: variant,
        action: isFavorite ? 'Remove' : 'Add'
      });
  }, [casino, gametype, variant, isFavorite]);

  const newProps = { ...rest, widgetId };

  const children = multiplayer ? (
    <>
      <PlayButton {...newProps} playType={PlayType.Single} />
      <PlayButton {...newProps} playType={PlayType.Multi} />
    </>
  ) : (
    <RoundPlayButton title={name} onClick={() => rest.onClick()} />
  );

  return hover ? (
    <div className={classNames(styles.container, isTouchSupported && styles.touch, className)}>
      <div className={styles.cornerButtonContainer}>
        {(!isEmbedded || isDesktopEmbedded) && !hideFeatureButtons && (
          <div className={styles.cornerButtonContainerRow}>
            {favoritesEnabled && (
              <FavoriteGameIcon
                classes={classNames(
                  styles.cornerButton,
                  styles.fav,
                  isFavorite && styles.activated
                )}
                onFavoriteClick={onFavoriteClick}
                favouriteIcon={favouriteIcon}
              />
            )}
            <GameInfoIcon className={infoIconClass} game={game} index={index} count={count} />
          </div>
        )}
      </div>
      {children}
    </div>
  ) : (
    children
  );
};

export default CasinoPlaySelector;
